import { useRef, useState } from 'preact/hooks';
import type { Theme } from '../styles/theme.js';
import { STRINGS } from '../strings.js';
import { AttachmentPreview, type PendingAttachment } from './AttachmentPreview.js';

/** Types proposés dans le sélecteur de fichiers — mêmes que ceux acceptés par le serveur. */
const ACCEPT = 'image/jpeg,image/png,image/webp';
const MAX_ATTACHMENTS = 4;

export type ComposedAttachment = { id: string; url: string };

type Props = {
  onSend: (content: string, attachments: ComposedAttachment[]) => void;
  /**
   * Téléverse le fichier et résout l'id + l'URL signée. Rejette avec un
   * message déjà en français, affichable tel quel.
   */
  onUpload: (file: File) => Promise<ComposedAttachment>;
  disabled: boolean;
  theme: Theme;
};

function PaperclipIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="1.8"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
    >
      <path d="M21.44 11.05 12.25 20.24a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
    </svg>
  );
}

export function InputComposer({ onSend, onUpload, disabled, theme }: Props) {
  const [value, setValue] = useState('');
  const [pending, setPending] = useState<PendingAttachment[]>([]);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const ready = pending.filter((p) => p.status === 'ready');
  const uploading = pending.some((p) => p.status === 'uploading');
  const canSend = !disabled && !uploading && (value.trim().length > 0 || ready.length > 0);

  function removePending(key: string): void {
    setPending((prev) => {
      const item = prev.find((p) => p.key === key);
      if (item) URL.revokeObjectURL(item.localUrl);
      return prev.filter((p) => p.key !== key);
    });
  }

  async function handleFiles(files: FileList | null): Promise<void> {
    if (!files || files.length === 0) return;
    setError(null);
    for (const file of Array.from(files)) {
      // On relit `pending` via le setter pour compter les éléments déjà
      // ajoutés dans la même boucle (setState est asynchrone).
      let rejected = false;
      const key = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const localUrl = URL.createObjectURL(file);
      setPending((prev) => {
        if (prev.length >= MAX_ATTACHMENTS) {
          rejected = true;
          return prev;
        }
        return [...prev, { key, localUrl, status: 'uploading' }];
      });
      if (rejected) {
        URL.revokeObjectURL(localUrl);
        setError(STRINGS.attachErrorTooMany);
        continue;
      }
      try {
        const { id, url } = await onUpload(file);
        setPending((prev) =>
          prev.map((p) => (p.key === key ? { ...p, status: 'ready', id, url } : p)),
        );
      } catch (err) {
        const message =
          err instanceof Error && err.message ? err.message : STRINGS.attachErrorGeneric;
        setError(message);
        setPending((prev) => prev.map((p) => (p.key === key ? { ...p, status: 'error' } : p)));
      }
    }
  }

  function handleSubmit(e: Event): void {
    e.preventDefault();
    if (!canSend) return;
    onSend(
      value.trim(),
      ready.map((p) => ({ id: p.id!, url: p.url! })),
    );
    for (const p of pending) URL.revokeObjectURL(p.localUrl);
    setPending([]);
    setValue('');
    setError(null);
  }

  return (
    <div>
      <AttachmentPreview items={pending} onRemove={removePending} theme={theme} />
      {error && (
        <div style={{ padding: '6px 16px 0', fontSize: '12px', color: '#b3261e' }} role="alert">
          {error}
        </div>
      )}
      <form
        onSubmit={handleSubmit}
        style={{
          display: 'flex',
          gap: '8px',
          alignItems: 'center',
          padding: '12px 16px',
          borderTop: '1px solid #e8e8e8',
        }}
      >
        <input
          ref={fileInput}
          type="file"
          // `capture` volontairement absent : sur mobile le visiteur garde le
          // choix entre l'appareil photo et sa photothèque.
          accept={ACCEPT}
          multiple
          style={{ display: 'none' }}
          onChange={(e) => {
            const input = e.target as HTMLInputElement;
            void handleFiles(input.files).finally(() => {
              input.value = '';
            });
          }}
        />
        <button
          type="button"
          aria-label={STRINGS.attachAdd}
          title={STRINGS.attachAdd}
          disabled={disabled || pending.length >= MAX_ATTACHMENTS}
          onClick={() => fileInput.current?.click()}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '36px',
            height: '36px',
            flex: '0 0 auto',
            borderRadius: '50%',
            border: 'none',
            background: 'transparent',
            color: '#666',
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled || pending.length >= MAX_ATTACHMENTS ? 0.4 : 1,
            padding: '0',
          }}
        >
          <PaperclipIcon />
        </button>
        <input
          type="text"
          value={value}
          onInput={(e) => setValue((e.target as HTMLInputElement).value)}
          placeholder={STRINGS.inputPlaceholder}
          aria-label={STRINGS.inputPlaceholder}
          disabled={disabled}
          maxLength={4000}
          style={{
            flex: 1,
            minWidth: '0',
            padding: '10px 12px',
            borderRadius: '20px',
            border: '1px solid #ddd',
            // 16px minimum : sous ce seuil, Safari iOS zoome automatiquement
            // la page au focus du champ.
            fontSize: '16px',
            outline: 'none',
          }}
        />
        <button
          type="submit"
          disabled={!canSend}
          style={{
            padding: '8px 16px',
            borderRadius: '20px',
            backgroundColor: theme.brandColor,
            color: '#fff',
            border: 'none',
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: canSend ? 1 : 0.5,
            fontSize: '14px',
            flex: '0 0 auto',
          }}
        >
          {STRINGS.send}
        </button>
      </form>
    </div>
  );
}
